import { asyncWithLDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import localLdOverrides from 'local-ld-overrides';

import { UUID_COOKIE_NAME, STASH_PROMO_CODE, STASH_PROMO_CODE_V2 } from 'stash/constants/session';
import { LAUNCH_DARKLY_CLIENT_IDS } from 'stash/constants';
import { getCookie } from 'stash/utils/cookies';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { userSelector } from 'stash/selectors/user';
import { useEffect, useRef } from 'react';
import { receiveNewExperimentFlagsAction } from 'stash/actions/experiments';
import { datadogRum } from '@datadog/browser-rum';
import { isMobileWeb, isLocal, isNativeWebview } from 'stash/utils';
import { getLocalStorage } from '../../utils/storage.js';
import { getSuperCookie } from 'stash/utils/superCookie';
import { pickUtmWithFallback } from 'stash/utils/attribution/pickUtmWithFallback';
import { EXPERIMENTS } from './keys';
import { resolveDeviceId } from './resolveDeviceId';

export { EXPERIMENTS };
export { resolveDeviceId } from './resolveDeviceId';

// Module-level cache of the stable browser device id resolved at boot.
// `getLaunchDarklyProvider` writes this value; `ExperimentsContextUpdater`
// reads it on later `client.identify(...)` calls so `device.key` /
// `referral.key` stay pinned across anonymous → identified transitions.
// Randomization unit for pre-signup experiments is `device`, so rewriting
// these keys to uuid-derived values would re-bucket every signup mid-funnel.
let cachedDeviceId;

/** Test-only: clear the module cache between cases. */
export function __resetCachedDeviceIdForTests() {
	cachedDeviceId = undefined;
}

function getDeviceId(deviceId) {
	if (typeof deviceId === 'string' && deviceId.trim()) {
		return deviceId.trim();
	}
	if (typeof cachedDeviceId === 'string' && cachedDeviceId.trim()) {
		return cachedDeviceId.trim();
	}
	const resolved = resolveDeviceId();
	cachedDeviceId = resolved.id;
	return resolved.id;
}

function createMultiContext(uuid, createDate, utmContent, utmCampaign, stashPromoCode, deviceId) {
	const stableDeviceId = getDeviceId(deviceId);
	const deviceContext = createDeviceContext(uuid, stableDeviceId);
	const referralContext = createReferralContext(
		uuid,
		utmContent,
		utmCampaign,
		stashPromoCode,
		stableDeviceId
	);

	const multi = {
		kind: 'multi',
		device: deviceContext,
		referral: referralContext,
	};

	// Only include a `user` kind when we have a real stashUUID. LaunchDarkly's
	// Segment integration forwards the `user` kind's key as Segment's `userId`
	// regardless of the `anonymous: true` flag; Mixpanel's Segment destination
	// then treats those events as coming from an identified user and uses that
	// value as `distinct_id` — which does not match the `$device:<ajs_anonymous_id>`
	// distinct_id Segment's client-side events use for anonymous visitors. By
	// omitting the user kind while anonymous, the LD → Segment event arrives
	// with only `anonymousId` set (sourced from the device kind's key), so
	// Mixpanel's destination lands on `$device:<ajs_anonymous_id>`, matching
	// client-side events. See STASH2026-2565.
	if (uuid) {
		multi.user = createUserContext(uuid, createDate);
	}

	return multi;
}

function createUserContext(uuid, createDate) {
	return {
		kind: 'user',
		key: uuid,
		createDate,
	};
}

/**
 * Device context is needed for targeting mobile web.
 * We cannot use the mobile SDK for mobile web.
 * `device.key` is always the stable browser device id (never uuid-derived).
 */
function createDeviceContext(uuid, deviceId) {
	const context = {
		kind: 'device',
		key: deviceId,
		isMobile: isMobileWeb(),
		isNativeWebview: isNativeWebview(),
	};
	if (!uuid) {
		context.anonymous = true;
	}
	return context;
}

function createReferralContext(uuid, utmContent, utmCampaign, stashPromoCode, deviceId) {
	const context = {
		kind: 'referral',
		key: deviceId,
		// Launch Darkly requires a default value for the custom rules to function properly.
		utmContent: utmContent || 'no-utm-content', //example: dot-com_investments_NVDA
		utmCampaign: utmCampaign || 'no-utm-campaign', //example: getfive
		stashPromoCode: stashPromoCode || 'no-stash-promo-code', //example: stash promo referral code
	};
	if (!uuid) {
		context.anonymous = true;
	}
	return context;
}

function pickFirstStringValue(...values) {
	for (const rawValue of values) {
		const value = typeof rawValue === 'string' ? rawValue.trim() : '';
		if (value) {
			return value;
		}
	}

	return undefined;
}

function getReferralAttributionContext() {
	const superCookie = getSuperCookie();
	return {
		utmContent: pickUtmWithFallback(superCookie, 'utm_content', 'getReferralAttributionContext'),
		utmCampaign: pickUtmWithFallback(superCookie, 'utm_campaign', 'getReferralAttributionContext'),
		stashPromoCode: pickFirstStringValue(
			superCookie?.promoCode,
			superCookie?.legacyPromoCode,
			getCookie(STASH_PROMO_CODE_V2),
			getCookie(STASH_PROMO_CODE)
		),
	};
}

/**
 * @param {string} [deviceId] Stable browser device id from `resolveDeviceId().id`.
 *   Callers should resolve before boot; if omitted, a device id is resolved/minted.
 */
export const getLaunchDarklyProvider = (deviceId) => {
	const stableDeviceId = getDeviceId(deviceId);
	cachedDeviceId = stableDeviceId;
	const clientSideID = LAUNCH_DARKLY_CLIENT_IDS[window.Stash.env];
	const referralAttributionContext = getReferralAttributionContext();

	return asyncWithLDProvider({
		context: createMultiContext(
			getCookie(UUID_COOKIE_NAME),
			// createDate comes from the user record on identify. The boot cookie
			// (stashUserCreatedAt) is never populated with a real timestamp.
			undefined,
			referralAttributionContext.utmContent,
			referralAttributionContext.utmCampaign,
			referralAttributionContext.stashPromoCode,
			stableDeviceId
		),
		clientSideID,
		options: {
			streaming: false,
			disableSyncEventPost: true,
			inspectors: [
				{
					type: 'flag-used',
					name: 'dd-inspector',
					method: (key, detail) => {
						datadogRum.addFeatureFlagEvaluation(key, detail.value);
					},
				},
			],
		},
		reactOptions: {
			useCamelCaseFlagKeys: false,
		},
	});
};

export function ExperimentsContextUpdater() {
	const mountedRef = useRef(false);
	const client = useLDClient();
	const user = useSelector(userSelector);

	useEffect(() => {
		if (mountedRef.current) {
			const referralAttributionContext = getReferralAttributionContext();
			client.identify(
				createMultiContext(
					user?.uuid,
					user?.created_at,
					referralAttributionContext.utmContent,
					referralAttributionContext.utmCampaign,
					referralAttributionContext.stashPromoCode,
					cachedDeviceId
				)
			);
		}

		mountedRef.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.uuid, user?.created_at]);

	return null;
}

function coerceOverrideVariant(value) {
	const normalized = typeof value === 'string' ? value : String(value);

	switch (normalized) {
		case 'default':
			return undefined;
		case 'false':
			return false;
		case 'true':
			return true;
		default:
			return normalized;
	}
}

function useCurrentVariant(experiment) {
	const flags = useFlags();
	const localTestVariant = getLocalStorage(experiment);
	const hasLocalTestVariant = localTestVariant !== null && localTestVariant !== undefined;

	if (hasLocalTestVariant) {
		return coerceOverrideVariant(localTestVariant);
	}

	if (import.meta.env.DEV && Object.prototype.hasOwnProperty.call(localLdOverrides, experiment)) {
		return coerceOverrideVariant(localLdOverrides[experiment]);
	}

	return flags[experiment];
}

/**
 * Renders DOM elements when in the provided experiment variant.
 */
export function ExperimentVariant({ experiment, variant, defaultVariant = false, children }) {
	const currentVariant = useCurrentVariant(experiment);

	if (defaultVariant && currentVariant === undefined) {
		return children;
	}

	return variant === currentVariant ? children : null;
}

/**
 * Returns true if the user is in the provided experiment variant.
 * For pure business logic; useful when not rendering DOM elements.
 */
export function useIsInExperimentVariation(experiment, variant = true) {
	const currentVariant = useCurrentVariant(experiment);
	return variant === currentVariant;
}

/**
 * Return the variant string the user is in.
 * Useful for a/b/c tests when you just want the variant string the user has been assigned,
 * */
export function useCurrentExperimentVariant(experiment) {
	return useCurrentVariant(experiment);
}

const VariantPropType = PropTypes.oneOfType([PropTypes.bool, PropTypes.string]);

if (isLocal()) {
	window.experiments = EXPERIMENTS;

	window.setExperimentVariant = (experiment, variant) => {
		localStorage.setItem(experiment, variant);
	};
}

ExperimentVariant.propTypes = {
	experiment: PropTypes.string.isRequired,
	variant: VariantPropType.isRequired,
	defaultVariant: PropTypes.bool,
	children: PropTypes.node.isRequired,
};

export function useSyncBankLinkEarlyExperimentFlag() {
	const dispatch = useDispatch();
	const isEnabled = useIsInExperimentVariation(EXPERIMENTS.BANK_LINK_EARLY, true);

	useEffect(() => {
		if (dispatch) {
			dispatch(
				receiveNewExperimentFlagsAction({
					flags: { [EXPERIMENTS.BANK_LINK_EARLY]: isEnabled },
				})
			);
		}
	}, [isEnabled, dispatch]);
}
